'use client';

import React from 'react';
import { Tag, Sparkles, Check, ArrowRight, ShieldCheck, Clock } from 'lucide-react';

interface DealsViewProps {
  onApplyDeal: (promoCode: string) => void;
}

export function DealsView({ onApplyDeal }: DealsViewProps) {
  const deals = [
    {
      code: 'FLEETAVA20',
      title: 'First Journey 20% Discount',
      tag: 'WELCOME DEAL',
      discount: '20% OFF',
      desc: 'Get 20% discount on your first booking with fleetava. Valid across all economy, SUV, and luxury classes.',
      validUntil: 'October 31, 2026',
      terms: 'Minimum 2-day rental period.',
    },
    {
      code: 'WEEKEND15',
      title: 'Weekend Escape Special',
      tag: 'WEEKEND RATE',
      discount: '15% OFF',
      desc: 'Pick up Friday after 12:00 PM and return Monday before 10:00 AM for reduced weekend rate and unlimited mileage.',
      validUntil: 'Ongoing 2026',
      terms: 'Valid on Convertible and SUV vehicles.',
    },
    {
      code: 'CORP30',
      title: 'Monthly & Business Long-Term Pass',
      tag: 'LONG TERM',
      discount: '30% OFF',
      desc: 'Special rate for corporate accounts and digital nomads renting for 14 days or longer. Full insurance included.',
      validUntil: 'Ongoing 2026',
      terms: 'Requires minimum 14 consecutive rental days.',
    },
  ];

  return (
    <div className="w-full bg-slate-50 min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="mb-8">
          <span className="text-xs font-black tracking-wider uppercase text-[var(--color-primary)] block mb-1">
            EXCLUSIVE OFFERS & SAVINGS
          </span>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
            Special Deals & Promotions
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-xl">
            Save on your next road trip, family vacation, or corporate trip with our verified promo codes and discount vouchers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {deals.map((deal, idx) => (
            <div
              key={idx}
              className="bg-white rounded-3xl border border-slate-200 p-6 shadow-xs hover:shadow-xl transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 rounded-full bg-amber-50 text-[var(--color-primary)] border border-amber-200/60 text-[10px] font-extrabold tracking-wider uppercase">
                    {deal.tag}
                  </span>
                  <span className="text-2xl font-black text-[var(--color-primary)] font-mono">
                    {deal.discount}
                  </span>
                </div>

                <h3 className="text-lg font-extrabold text-slate-900 tracking-tight mb-2">
                  {deal.title}
                </h3>
                <p className="text-xs text-slate-500 leading-relaxed mb-6">
                  {deal.desc}
                </p>

                {/* Voucher Code Box */}
                <div className="p-3 bg-slate-50 border border-dashed border-slate-300 rounded-xl flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-[var(--color-primary)]" />
                    <span className="font-mono font-extrabold text-sm text-slate-800">
                      {deal.code}
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-semibold">1-Click Apply</span>
                </div>

                <div className="text-[11px] text-slate-400 space-y-1">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Valid until: {deal.validUntil}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                    <span>{deal.terms}</span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => onApplyDeal(deal.code)}
                className="mt-6 w-full py-3 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] active:scale-95 text-white text-xs font-extrabold uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>APPLY & BOOK WITH THIS DEAL</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
